import { CommonModule } from '@angular/common';
import { Component, ViewChild, computed, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BaseFormComponent } from '../../../../core/base-classes/base-form-component';
import { InputLabel } from '../../../../parts/input-label/input-label.component';
import { RichTextEditorComponent } from '../../../../parts/rich-text-editor/rich-text-editor.component';
import { EmailAttachmentsComponent } from '../../../communications/components/email-attachments/email-attachments.component';
import { AttachmentSelectionState } from '../../../../core/models/ui/email-attachment.model';
import { UserSessionService } from '../../../../core/services/user-session.service';

export interface ContactSupportFormValue {
    organizationId: number;
    title: string;
    body: string;
    attachmentDocumentIds: number[];
}

@Component({
    selector: 'ms-contact-support-form',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, InputLabel, RichTextEditorComponent, EmailAttachmentsComponent],
    templateUrl: './contact-support-form.component.html'
})
export class ContactSupportFormComponent extends BaseFormComponent {

    private formBuilder = inject(FormBuilder);
    private userSessionService = inject(UserSessionService);

    // The page can only render with an active organization (support tickets are org-scoped), so this is
    // always defined at render time. Kept as a computed so the template can bind directly to the id.
    public organizationId = computed<number | undefined>(() => this.userSessionService.activeOrganization()?.organizationId);

    public supportForm: FormGroup = this.formBuilder.group({
        title: ['', [Validators.required, Validators.maxLength(150)]],
        body: ['', [Validators.required]]
    });

    @ViewChild(EmailAttachmentsComponent) private emailAttachments?: EmailAttachmentsComponent;

    // Snapshot of the attachments component's latest emitted state. Used to build the submit payload and
    // to block submit while an upload is still committing or the combined size ceiling is exceeded.
    public attachmentState = signal<AttachmentSelectionState>({
        attachments: [],
        totalBytes: 0,
        isWithinLimit: true,
        isUploading: false
    });

    public get isValid(): boolean {
        const state = this.attachmentState();
        return this.organizationId() !== undefined
            && this.supportForm.valid
            && state.isWithinLimit
            && !state.isUploading;
    }

    public onAttachmentsChanged(state: AttachmentSelectionState): void {
        this.attachmentState.set(state);
    }

    public markAllTouched(): void {
        this.supportForm.markAllAsTouched();
    }

    public reset(): void {
        this.supportForm.reset({ title: '', body: '' });
        // The child's reset() re-emits attachmentsChanged via its effect, which flows back through
        // onAttachmentsChanged and clears our local attachmentState signal.
        this.emailAttachments?.reset();
    }

    public getValue(): ContactSupportFormValue {
        const orgId = this.organizationId();
        if (orgId === undefined) {
            throw new Error('Cannot build support ticket payload without an active organization.');
        }
        const raw = this.supportForm.getRawValue() as { title: string; body: string };
        return {
            organizationId: orgId,
            title: raw.title,
            body: raw.body,
            attachmentDocumentIds: this.attachmentState().attachments.map(a => a.documentId)
        };
    }
}
